import {
  makeStyles,
  tokens,
  Text,
  Tooltip,
  ProgressBar,
} from '@fluentui/react-components';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    minHeight: '24px',
  },
  containerWarning: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    borderBottom: `1px solid ${tokens.colorPaletteYellowBorder1}`,
  },
  containerCompacting: {
    backgroundColor: tokens.colorPaletteBlueBorderActive,
    borderBottom: `1px solid ${tokens.colorPaletteBlueBorderActive}`,
  },
  label: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  labelWarning: {
    color: tokens.colorPaletteYellowForeground2,
  },
  progressWrapper: {
    flex: 1,
    maxWidth: '100px',
  },
  percentText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
    minWidth: '28px',
    textAlign: 'right' as const,
  },
});

/**
 * Compact context window usage indicator shown below the ChatHeader.
 * Displays a small progress bar with usage percentage.
 * Only visible when contextUsagePercent is available (Copilot SDK sessions).
 */
export const ContextStatusIndicator = () => {
  const styles = useStyles();
  const contextUsagePercent = useAgentChatAguiStore((s) => s.contextUsagePercent);
  const contextStatus = useAgentChatAguiStore((s) => s.contextStatus);

  // Don't render if no context info available
  if (contextUsagePercent == null && contextStatus == null) {
    return null;
  }

  const percent = contextUsagePercent ?? 0;
  const percentDisplay = Math.round(percent * 100);
  const isWarning = contextStatus === 'warning' || percent >= 0.6;
  const isCompacting = contextStatus === 'compacting';

  const tooltipContent = isCompacting
    ? 'Conversation history is being compacted to free up context space...'
    : `Context window is ${percentDisplay}% full.${isWarning ? ' Compaction will begin soon.' : ''}`;

  const barColor = isCompacting
    ? 'brand'
    : isWarning
      ? 'warning'
      : 'success';

  return (
    <Tooltip content={tooltipContent} relationship="description" positioning="below">
      <div
        className={`${styles.container} ${isWarning ? styles.containerWarning : ''} ${isCompacting ? styles.containerCompacting : ''}`}
      >
        <Text className={`${styles.label} ${isWarning ? styles.labelWarning : ''}`}>
          {isCompacting ? '🔄 Compacting...' : 'Context'}
        </Text>
        <div className={styles.progressWrapper}>
          <ProgressBar
            value={percent}
            max={1}
            color={barColor}
            thickness="medium"
          />
        </div>
        <Text className={styles.percentText}>
          {percentDisplay}%
        </Text>
      </div>
    </Tooltip>
  );
};
